import { Users, Target, Award, Heart, CheckCircle } from 'lucide-react';
import PromotionalBanner from '../components/ui/PromotionalBanner';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from course content to student support.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We believe in building a strong learning community where students support each other.',
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'All our courses are carefully crafted by industry experts with real-world experience.',
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'We are passionate about education and helping people achieve their learning goals.',
    },
  ];

  const author = {
    name: 'Karthik KK',
    role: 'Founder & AI Testing Expert',
    image: '/images/karthik-profile.jpg',
    bio: '20+ years in test automation and AI. Creator of multiple Udemy courses on AI testing and GenAI. Passionate about empowering QA professionals with cutting-edge AI testing skills.',
    achievements: [
      '370,000+ students taught worldwide',
      '40+ courses created on AI and testing',
      '20+ years of industry experience',
      'AI testing frameworks specialist'
    ]
  };

  const stats = [
    { label: 'Students Worldwide', value: '370,000+' },
    { label: 'Total Courses', value: '40+' },
    { label: 'AI Testing Courses', value: '5' },
    { label: 'Years Experience', value: '20+' },
    { label: 'Countries Reached', value: '198' },
  ];

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/20 to-emerald-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              About <span className="text-green-100">ExecuteAutomation</span>
            </h1>
            <p className="text-xl md:text-2xl text-green-50 max-w-4xl mx-auto leading-relaxed">
              We're pioneering the future of software testing through AI and GenAI technologies.
              Join thousands of QA professionals advancing their careers with cutting-edge AI testing skills.
            </p>
          </div>
        </div>
      </section>

      {/* Promotional Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <PromotionalBanner 
          variant="inline"
          className="max-w-4xl mx-auto"
        />
      </section>

      {/* Mission Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Our <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Mission</span>
            </h2>
            <p className="text-lg text-secondary-600 mb-6">
              At ExecuteAutomation, we specialize in AI and Generative AI testing education.
              Our mission is to empower QA professionals and testers with the advanced AI skills
              needed to excel in the rapidly evolving landscape of software testing and quality assurance.
            </p>
            <p className="text-lg text-secondary-600">
              Whether you're a seasoned QA professional or new to testing, we provide
              comprehensive training in AI testing frameworks, multi-agent systems,
              LangChain, RAG implementations, and cutting-edge GenAI testing methodologies.
            </p>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
              alt="Students learning together"
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Our Impact
            </h2>
            <p className="text-xl text-secondary-600">
              Numbers that showcase our commitment to quality education
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-secondary-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Global Impact
            </h2>
            <p className="text-xl text-green-50 max-w-3xl mx-auto">
              Our courses reach students across 198 countries, creating a worldwide community of AI testing professionals
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 transform transition-all duration-500 hover:scale-110 hover:bg-white/20 hover:shadow-2xl hover:shadow-emerald-500/20 cursor-pointer group">
              <div className="text-4xl font-bold mb-2 group-hover:text-green-200 transition-colors duration-300 group-hover:animate-pulse">198</div>
              <div className="text-green-100 group-hover:text-white transition-colors duration-300">Countries Reached</div>
              <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-1 bg-green-300 rounded-full mx-auto animate-pulse"></div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 transform transition-all duration-500 hover:scale-110 hover:bg-white/20 hover:shadow-2xl hover:shadow-emerald-500/20 cursor-pointer group">
              <div className="text-4xl font-bold mb-2 group-hover:text-green-200 transition-colors duration-300 group-hover:animate-bounce">370K+</div>
              <div className="text-green-100 group-hover:text-white transition-colors duration-300">Students Worldwide</div>
              <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-1 bg-green-300 rounded-full mx-auto animate-pulse"></div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 transform transition-all duration-500 hover:scale-110 hover:bg-white/20 hover:shadow-2xl hover:shadow-emerald-500/20 cursor-pointer group">
              <div className="text-4xl font-bold mb-2 group-hover:text-green-200 transition-colors duration-300 group-hover:animate-pulse">40+</div>
              <div className="text-green-100 group-hover:text-white transition-colors duration-300">Courses Available</div>
              <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-1 bg-green-300 rounded-full mx-auto animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-secondary-900 mb-4">
            Our Values
          </h2>
          <p className="text-xl text-secondary-600">
            The principles that guide everything we do
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div key={index} className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-secondary-600">{value.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Author Section */}
      <section className="bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Meet Our Author
            </h2>
            <p className="text-xl text-secondary-600">
              The AI testing expert behind ExecuteAutomation
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center lg:text-left">
                  <img
                    src={author.image}
                    alt={author.name}
                    className="w-48 h-auto max-h-64 rounded-2xl object-contain mx-auto lg:mx-0 mb-6 shadow-lg bg-gray-50"
                    onError={(e) => {
                      // Fallback to a professional placeholder if your image doesn't load
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face';
                    }}
                  />
                  <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                    {author.name}
                  </h3>
                  <p className="text-green-600 font-semibold mb-4 text-lg">{author.role}</p>
                </div>
                <div>
                  <p className="text-secondary-600 mb-6 text-lg leading-relaxed">
                    {author.bio}
                  </p>
                  <div className="space-y-3">
                    {author.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-secondary-700">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Modern Green Theme */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-400/10 rounded-full blur-2xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              Ready to Start <span className="bg-gradient-to-r from-green-100 to-emerald-100 bg-clip-text text-transparent">Learning</span>?
            </h2>
            <p className="text-lg md:text-xl text-green-50 mb-8 max-w-3xl mx-auto leading-relaxed">
              Join 370,000+ learners who have transformed their careers with AI testing skills. 
              Master cutting-edge GenAI testing methodologies and advance your QA career today.
            </p>
            
            {/* Enhanced buttons with better alignment */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
              <a
                href="/courses"
                className="w-full sm:w-auto bg-white text-green-600 px-8 py-4 rounded-xl font-semibold hover:bg-green-50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg shadow-md"
              >
                Browse AI Courses
              </a>
              <a
                href="/paths"
                className="w-full sm:w-auto border-2 border-white/80 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
              >
                View Learning Paths
              </a>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-sm text-green-100 mb-3">Trusted by professionals at</p>
              <div className="flex justify-center items-center space-x-6 opacity-70">
                <span className="text-white font-medium">Google</span>
                <span className="text-white/60">•</span>
                <span className="text-white font-medium">Microsoft</span>
                <span className="text-white/60">•</span>
                <span className="text-white font-medium">Amazon</span>
                <span className="text-white/60">•</span>
                <span className="text-white font-medium">Meta</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;